/*
 * School Workspace Setup (Part 3) — public invite-accept flow.
 * A school's primary contact opens /school-invite/:token to activate their
 * workspace: confirm details → terms + no-student-data ack → create login →
 * routed to the Diagnostic.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { toast } from "@/components/ui/sonner";
import { Loader2, School, ShieldCheck, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SchoolWorkspaceSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", title: "", phone: "", password: "", terms: false, nsd: false });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/school-invite/${token}`);
      if (r.data.expired) setError("This invitation has expired. Please ask your portfolio manager for a new link.");
      else if (r.data.already_accepted) setError("This invitation has already been used. Please sign in at /diagnostic/login.");
      else {
        setInfo(r.data);
        setForm((f) => ({ ...f, first_name: r.data.contact.first_name || "", last_name: r.data.contact.last_name || "", title: r.data.contact.title || "" }));
      }
    } catch { setError("This invitation link is invalid."); } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!form.terms || !form.nsd) { toast.error("Please accept both acknowledgments"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/school-invite/${token}/accept`, {
        password: form.password, first_name: form.first_name, last_name: form.last_name,
        title: form.title, phone: form.phone, accepted_terms: form.terms, no_student_data_ack: form.nsd,
      });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation failed"); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9fafb] px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#e5e7eb] p-8 text-center" data-testid="invite-error">
        <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
        <p className="mt-3 text-[14px] text-[#374151]">{error}</p>
        <Button onClick={() => navigate("/diagnostic/login")} variant="outline" className="mt-5">Go to sign in</Button>
      </div>
    </div>
  );

  const sch = info.school;

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="school-workspace-setup">
      <header className="bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 flex items-center h-10"><img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-12 w-auto" /></div>
      </header>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
        {done ? (
          <div className="bg-white rounded-2xl border border-[#e5e7eb] p-10 text-center" data-testid="invite-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-[#111827]">Workspace activated!</h1>
            <p className="mt-2 text-[14px] text-[#4b5563]">Your MySchoolRx workspace for <strong>{sch.name}</strong> is ready. Sign in to begin the Diagnostic.</p>
            <Button onClick={() => navigate("/diagnostic/login")} className="mt-6 bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="invite-goto-login">Sign in &amp; Start Diagnostic <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#e5e7eb] p-6 sm:p-8">
            <span className="text-[11px] uppercase tracking-[0.16em] text-[#007a9e] font-semibold">Invited by {info.portfolio_name}</span>
            <h1 className="mt-1 text-2xl font-bold text-[#111827]">Activate Your School Workspace</h1>
            <p className="mt-2 text-[14px] text-[#6b7280]">Your school has been added to a licensed Portfolio. Complete setup to begin MySchoolRx.</p>

            {step === 0 && (
              <div className="mt-6" data-testid="setup-step-details">
                <div className="rounded-xl border border-[#e5e7eb] p-4 bg-[#f9fafb]">
                  <div className="flex items-center gap-2 text-[#111827] font-semibold"><School className="h-4 w-4 text-[#007a9e]" />{sch.name}</div>
                  <div className="mt-1 text-[13px] text-[#6b7280]">
                    {[sch.street_address, sch.city, sch.state, sch.zip].filter(Boolean).join(", ")}
                    {sch.nces_school_id && <div>NCES ID: {sch.nces_school_id}</div>}
                    {sch.lea_name && <div>District/LEA: {sch.lea_name}</div>}
                  </div>
                </div>
                <p className="mt-3 text-[12px] text-[#9ca3af]">Confirm this is your school. You can refine details inside your workspace later.</p>
                <div className="mt-5 flex justify-end"><Button onClick={() => setStep(1)} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="setup-details-next">Confirm &amp; Continue <ArrowRight className="h-4 w-4 ml-2" /></Button></div>
              </div>
            )}

            {step === 1 && (
              <div className="mt-6 space-y-4" data-testid="setup-step-account">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="setup-first" />
                  <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="setup-last" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  <PhoneField placeholder="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="setup-phone" />
                </div>
                <Input type="password" placeholder="Create a password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="setup-password" />
                <label className="flex items-start gap-2 text-[13px] text-[#374151]"><input type="checkbox" checked={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.checked })} className="mt-1" data-testid="setup-terms" /> I accept the Terms of Use and Diagnostic use terms.</label>
                <label className="flex items-start gap-2 text-[13px] text-[#374151]"><input type="checkbox" checked={form.nsd} onChange={(e) => setForm({ ...form, nsd: e.target.checked })} className="mt-1" data-testid="setup-nsd" /> I acknowledge MySchoolRx collects school-level operational, governance, compliance, fiscal, instructional, and performance evidence. I will not upload student-level personally identifiable information.</label>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
                  <Button disabled={submitting} onClick={submit} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="setup-activate">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Activating…</> : <><ShieldCheck className="h-4 w-4 mr-2" />Activate Workspace</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
